import { useRouter } from 'next/router'
import type { FunctionComponent } from 'react'

import type { Values } from './common'
import { EnterCode } from './common'
import { SsoType } from '@app-types/sso'
import type { FormProps } from '@contentful-components/forms/props'
import { CustomFieldId, useField } from '@graphql/hooks/field'
import { CustomFieldsetId } from '@graphql/hooks/form-page'
import { CustomModalId, useModal } from '@graphql/hooks/modal'
import { modalStore } from '@lib/store/ui/modal'
import { useSession, useStores } from '@lib/store/useStores'
import { LoginStatus, OtpVerificationStatus } from '@lib/utils/login'
import { renderTemplate } from '@lib/utils/text'
import { LINK } from '@shared/util/link'
import { PHONE_FORMAT } from '@shared/util/phone'

/**
 * This form verifies OTP for given phone.
 */
export const EnterPhoneCode: FunctionComponent<
    React.PropsWithChildren<FormProps<Values>>
> = ({ next, title, renderSubmit }) => {
    const { linkingStore } = useStores()
    const { phoneVerificationStore, login } = useSession()
    const router = useRouter()
    const alreadySignedUp = useModal(CustomModalId.KycAlreadySignedUp)
    const { errorTexts } = useField(CustomFieldId.LoginOtp)

    return (
        <EnterCode
            store={phoneVerificationStore}
            next={next}
            customFieldsetId={CustomFieldsetId.LoginEnterCode}
            title={renderTemplate(title, {
                value: phoneVerificationStore.valueToVerify || PHONE_FORMAT,
            })}
            renderSubmit={renderSubmit}
            onSubmit={async (otp, { setError }) => {
                const result =
                    await phoneVerificationStore.completeVerification(otp)

                switch (result.status) {
                    case OtpVerificationStatus.NO_SESSION: {
                        await router.push(LINK.login(next).serialize(router))
                        return
                    }
                    case OtpVerificationStatus.SUCCESS: {
                        const status = await login()

                        if (status === LoginStatus.IN_RECOVERY) {
                            const editNumber = !(await modalStore.prompt(
                                alreadySignedUp.modal,
                            ))

                            if (editNumber) {
                                await router.push(
                                    LINK.login(next).serialize(router),
                                )
                            }

                            const link = linkingStore.defaultLink
                            if (link) window.location.assign(link)
                            return
                        }

                        await router.push(
                            LINK.sso(SsoType.Email, next).serialize(router),
                        )
                        return
                    }
                    case OtpVerificationStatus.FAIL: {
                        setError('otp', {
                            message: errorTexts.validate,
                        })
                        return
                    }
                    case OtpVerificationStatus.ERROR: {
                        setError('otp', {
                            message: result.error.userFriendlyDescription,
                        })
                        return
                    }
                    default: {
                        setError('otp', {
                            message: errorTexts.connectionFailure,
                        })
                        return
                    }
                }
            }}
        />
    )
}
